import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, CheckCircle2, ClipboardCheck, Film, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { fmtDateBR } from "@/lib/labels";
import { ANALYTICS_COLORS, ChartCard, EmptyAnalytics, MetricCard, formatPercent } from "@/components/analytics/AnalyticsPrimitives";

const STATUS_STYLE = {
  PRESENT: "bg-[#E6F4EA] text-[#2F855A] border-[#B9DFC7]",
  JUSTIFIED: "bg-[#FEEBC8] text-[#C05621] border-[#F2CE93]",
  ABSENT: "bg-[#FED7D7] text-[#C53030] border-[#F0B6B6]",
};
const STATUS_LABEL = { PRESENT: "P", JUSTIFIED: "J", ABSENT: "F" };
const STATUS_TITLE = { PRESENT: "Presente", JUSTIFIED: "Falta justificada", ABSENT: "Falta" };
const EVAL_STATE = { NOT_STARTED: "Não fez", IN_PROGRESS: "Em andamento", NEEDS_GRADING: "Aguardando correção", GRADED: "Corrigida", SUBMITTED: "Enviada", AUTO_SUBMITTED: "Enviada (tempo)", EXPIRED: "Expirada" };

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E0D9CD] rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold mb-1">{fmtDateBR(label)}</p>
      {payload.map((item) => <p key={item.dataKey} className="text-[#6B655E]"><strong className="text-[#1F1F1F]">{item.name}:</strong> {item.value == null ? "—" : `${item.value}%`}</p>)}
    </div>
  );
}

function EvalTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return <div className="bg-white border border-[#E0D9CD] rounded-xl shadow-lg px-3 py-2 text-xs max-w-[220px]"><p className="font-semibold break-words">{row?.title}</p><p className="text-[#6B655E] mt-1">Nota: <strong className="text-[#1F1F1F]">{row?.score}</strong></p></div>;
}

export default function StudentAnalyticsSummary({ analytics, classification }) {
  const { can } = usePermissions();
  const [evaluations, setEvaluations] = useState(undefined);
  const canViewEvaluations = can("eval.view_results");

  useEffect(() => {
    const studentId = analytics?.student?.id;
    if (!studentId || !canViewEvaluations) { setEvaluations(null); return; }
    let alive = true;
    setEvaluations(undefined);
    api.get(`/analytics/students/${studentId}/evaluations`)
      .then(({ data }) => alive && setEvaluations(data))
      .catch(() => alive && setEvaluations(null));
    return () => { alive = false; };
  }, [analytics?.student?.id, canViewEvaluations]);

  if (!analytics) return <EmptyAnalytics title="Carregando resumo do aluno…" />;
  const summary = analytics.summary || {};
  const trend = analytics.attendance_trend || [];
  const recent = analytics.recent_attendance || [];
  const video = analytics.video || {};
  const compare = [
    { label: "Aluno", percent: summary.frequency ?? 0 },
    { label: "Turma", percent: summary.class_average ?? 0 },
  ];
  const evalSummary = evaluations?.summary || {};

  return (
    <div className="space-y-4" data-testid="student-analytics-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard label="Frequência" value={formatPercent(summary.frequency)} icon={TrendingUp} tone={summary.frequency != null && summary.frequency < 75 ? "critical" : "success"} hint={summary.valid_lessons ? `${summary.present || 0} presença(s) em ${summary.valid_lessons} aula(s) válida(s)` : "Sem aulas válidas"} />
        <MetricCard label="Média da turma" value={formatPercent(summary.class_average)} icon={CheckCircle2} tone="info" hint="Comparação agregada, sem expor outros alunos" />
        <MetricCard label="Faltas" value={summary.unjustified_absent || 0} icon={AlertTriangle} tone={(summary.unjustified_absent || 0) >= 4 ? "critical" : "warning"} hint={`${summary.justified || 0} justificada(s)`} />
        <MetricCard label="Vídeos concluídos" value={`${video.completed || 0}/${video.total || 0}`} icon={Film} tone="orange" hint={`${video.in_progress || 0} em andamento`} />
      </div>

      {classification && (
        <div className="rounded-xl border border-[#EBE5DB] bg-[#FAF7F1] px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs text-[#6B655E]">Classificação atual</p>
            <p className="font-display text-lg font-bold">{classification === "CRITICAL" ? "Crítico" : classification === "ATTENTION" ? "Atenção" : "Em dia"}</p>
          </div>
          <p className="text-xs text-[#6B655E] max-w-md">A classificação continua sendo calculada pelas regras atuais do sistema; o resumo apenas explica os dados que ajudam a entendê-la.</p>
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-4">
        <ChartCard title="Evolução da frequência" subtitle="Percentual acumulado do aluno ao longo das aulas válidas.">
          {trend.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 8, right: 8, left: -18, bottom: 6 }} accessibilityLayer>
                  <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" />
                  <XAxis dataKey="lesson_date" tickFormatter={(v) => fmtDateBR(v)} tick={{ fontSize: 10 }} minTickGap={24} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<TooltipBox />} />
                  <Line type="monotone" dataKey="cumulative_percent" name="Frequência" stroke={ANALYTICS_COLORS.orange} strokeWidth={3} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyAnalytics description="Ainda não há aulas válidas suficientes para exibir evolução." />}
        </ChartCard>

        <ChartCard title="Aluno x média da turma" subtitle="Comparação simples para dar contexto, sem ranking de pessoas.">
          {summary.frequency != null || summary.class_average != null ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compare} margin={{ top: 8, right: 10, left: -18, bottom: 6 }} accessibilityLayer>
                  <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(value) => [`${value}%`, "Frequência"]} />
                  <Bar dataKey="percent" name="Frequência" fill={ANALYTICS_COLORS.orange} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyAnalytics />}
        </ChartCard>
      </div>

      <ChartCard title="Últimas aulas" subtitle="Uma leitura rápida da sequência recente. P = presente · J = justificada · F = falta.">
        {recent.length ? (
          <div className="flex flex-wrap gap-2">
            {recent.map((item, index) => (
              <div key={item.lesson_id} className="text-center">
                <div className={`w-10 h-10 rounded-xl border flex items-center justify-center font-bold text-sm ${STATUS_STYLE[item.status] || STATUS_STYLE.ABSENT}`} title={`${item.title} · ${STATUS_TITLE[item.status] || "Falta"}`}>
                  {STATUS_LABEL[item.status] || "F"}
                </div>
                <p className="text-[10px] text-[#6B655E] mt-1">{index + 1}</p>
              </div>
            ))}
          </div>
        ) : <EmptyAnalytics description="Sem aulas válidas no histórico atual." />}
      </ChartCard>

      {canViewEvaluations && evaluations === undefined && <div className="h-44 rounded-2xl bg-[#F0EBE3] animate-pulse" />}
      {canViewEvaluations && evaluations && (
        <ChartCard title="Avaliações" subtitle="Desempenho individual nas avaliações destinadas a este aluno. A linha mostra somente provas com nota calculada.">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <MetricCard label="Realizadas" value={`${evalSummary.completed || 0}/${evalSummary.eligible || 0}`} icon={ClipboardCheck} tone="info" />
            <MetricCard label="Média" value={evalSummary.average_score ?? "—"} tone="orange" />
            <MetricCard label="Aprovações" value={evalSummary.approved || 0} tone="success" hint={formatPercent(evalSummary.approval_rate)} />
            <MetricCard label="P/ corrigir" value={evalSummary.pending_grading || 0} tone={(evalSummary.pending_grading || 0) ? "warning" : "neutral"} />
          </div>
          {(evaluations.score_trend || []).length ? (
            <div className="h-64 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evaluations.score_trend} margin={{ top: 8, right: 8, left: -18, bottom: 50 }} accessibilityLayer>
                  <CartesianGrid stroke="#EEE8DE" strokeDasharray="3 3" />
                  <XAxis dataKey="title" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" interval={0} height={70} />
                  <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                  <Tooltip content={<EvalTooltip />} />
                  <Line type="monotone" dataKey="score" name="Nota" stroke={ANALYTICS_COLORS.blue} strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyAnalytics description="Ainda não há notas calculadas para mostrar evolução." />}
          <div className="divide-y divide-[#EBE5DB] mt-3">
            {(evaluations.rows || []).slice(-5).reverse().map((row) => (
              <div key={row.evaluation_id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0"><p className="font-medium truncate" title={row.title}>{row.title}</p><p className="text-xs text-[#6B655E]">{EVAL_STATE[row.status] || row.status} · {row.attempts_used || 0} tentativa(s)</p></div>
                <div className="flex items-center gap-3 shrink-0"><span className="font-display font-bold">{row.final_score ?? "—"}</span><Link to={`/evaluations/${row.evaluation_id}/analytics`} className="text-xs text-[#F75A1B] hover:underline">Analisar</Link></div>
              </div>
            ))}
          </div>
        </ChartCard>
      )}
    </div>
  );
}
